import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Check } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Confetti from "@/components/Confetti";

export default function ThisOrThatModal({ open, matchId, otherName, onClose }) {
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);

    const start = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/games/tot/start", { match_id: matchId });
            setSession(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't start game");
            onClose?.();
        } finally {
            setLoading(false);
        }
    };

    const refresh = async () => {
        if (!session?.session_id) return;
        try {
            const { data } = await api.get(`/games/tot/${session.session_id}`);
            const prevScore = session.score;
            setSession(data);
            if (data.score != null && prevScore == null) {
                setShowConfetti(true);
                setTimeout(() => setShowConfetti(false), 3000);
            }
        } catch (e) {}
    };

    useEffect(() => {
        if (!open) {
            setSession(null);
            setShowConfetti(false);
            return;
        }
        start();
    }, [open]);

    useEffect(() => {
        if (!session || session.score != null || !session.my_done) return;
        // Poll while waiting for other player
        const t = setInterval(refresh, 5000);
        return () => clearInterval(t);
    }, [session]);

    const answer = async (choice) => {
        if (submitting || !session) return;
        const q = session.questions.find((qq) => !session.my_answers?.[qq.id] && session.my_answers?.[qq.id] !== "a" && session.my_answers?.[qq.id] !== "b") || session.questions.find((qq) => !(String(qq.id) in (session.my_answers || {})));
        if (!q) return;
        setSubmitting(true);
        // Optimistic
        setSession((s) => ({
            ...s,
            my_answers: { ...(s.my_answers || {}), [q.id]: choice },
        }));
        try {
            const { data } = await api.post(`/games/tot/${session.session_id}/answer`, {
                question_id: q.id,
                choice,
            });
            const prevScore = session.score;
            setSession(data);
            if (data.score != null && prevScore == null) {
                setShowConfetti(true);
                setTimeout(() => setShowConfetti(false), 3000);
            }
        } catch (e) {
            toast.error("Couldn't record answer");
            // Rollback
            setSession((s) => {
                const na = { ...(s.my_answers || {}) };
                delete na[q.id];
                return { ...s, my_answers: na };
            });
        } finally {
            setSubmitting(false);
        }
    };

    if (!open) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
                data-testid="tot-modal"
                className="fixed inset-0 z-[85] bg-background/80 backdrop-blur-xl grid place-items-center p-4"
            >
                <motion.div
                    initial={{ scale: 0.9, y: 20, opacity: 0 }}
                    animate={{ scale: 1, y: 0, opacity: 1 }}
                    exit={{ scale: 0.9, y: 20, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 260, damping: 25 }}
                    onClick={(e) => e.stopPropagation()}
                    className="relative w-full max-w-md rounded-4xl border-2 border-foreground bg-card p-5 pb-6"
                >
                    {showConfetti && <Confetti />}
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <div className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-1">
                                <Sparkles className="h-3 w-3" strokeWidth={3} /> vibe game
                            </div>
                            <div className="font-display text-2xl font-black leading-tight">
                                This or <span className="font-fancy text-gradient-purple">that</span>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            data-testid="tot-close-btn"
                            className="h-9 w-9 rounded-full hover:bg-accent grid place-items-center"
                        >
                            <X className="h-5 w-5" strokeWidth={2.5} />
                        </button>
                    </div>

                    {loading || !session ? (
                        <div className="text-center text-sm text-muted-foreground py-16">Loading the game…</div>
                    ) : session.score != null ? (
                        <ResultView session={session} otherName={otherName} onClose={onClose} />
                    ) : session.my_done ? (
                        <WaitingView otherName={otherName} onRefresh={refresh} />
                    ) : (
                        <QuestionView session={session} onAnswer={answer} submitting={submitting} />
                    )}
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

function QuestionView({ session, onAnswer, submitting }) {
    // Find the next unanswered question
    const answered = session.my_answers || {};
    const total = session.questions.length;
    const currentIdx = session.questions.findIndex((q) => !(String(q.id) in answered));
    const q = currentIdx >= 0 ? session.questions[currentIdx] : null;
    const done = currentIdx < 0;
    const progress = done ? total : currentIdx;

    if (!q) {
        return <div className="text-center py-10 text-sm text-muted-foreground">All done!</div>;
    }

    return (
        <div data-testid="tot-question-view">
            {/* Progress */}
            <div className="mb-4">
                <div className="flex justify-between text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">
                    <span>Question {progress + 1} / {total}</span>
                    <span>{Math.round(((progress) / total) * 100)}%</span>
                </div>
                <div className="h-2 rounded-full bg-accent overflow-hidden">
                    <div
                        className="h-full bg-primary transition-all"
                        style={{ width: `${((progress) / total) * 100}%` }}
                    />
                </div>
            </div>

            <div className="text-center py-4">
                <div className="font-display text-2xl font-black">{q.prompt}</div>
            </div>

            {/* Options */}
            <div className="space-y-3 mt-3">
                <button
                    onClick={() => onAnswer("a")}
                    disabled={submitting}
                    data-testid="tot-option-a"
                    className="w-full h-16 rounded-3xl border-2 border-foreground bg-card hover:bg-primary hover:text-primary-foreground transition font-display font-black text-lg lift disabled:opacity-50"
                >
                    {q.a}
                </button>
                <div className="text-center text-[10px] font-black uppercase tracking-widest text-muted-foreground">or</div>
                <button
                    onClick={() => onAnswer("b")}
                    disabled={submitting}
                    data-testid="tot-option-b"
                    className="w-full h-16 rounded-3xl border-2 border-foreground bg-card hover:bg-primary hover:text-primary-foreground transition font-display font-black text-lg lift disabled:opacity-50"
                >
                    {q.b}
                </button>
            </div>
        </div>
    );
}

function WaitingView({ otherName, onRefresh }) {
    return (
        <div className="py-8 text-center" data-testid="tot-waiting-view">
            <div className="mx-auto h-16 w-16 rounded-3xl bg-accent grid place-items-center text-3xl">
                🕒
            </div>
            <div className="mt-4 font-display text-xl font-black">
                Nice — you're done!
            </div>
            <p className="text-sm text-muted-foreground mt-1 px-4">
                Waiting for {otherName || "your match"} to finish their picks. We'll ping you when the results drop.
            </p>
            <button
                onClick={onRefresh}
                data-testid="tot-refresh-btn"
                className="mt-4 h-11 px-6 rounded-2xl bg-primary text-primary-foreground font-black text-sm lift"
            >
                Check again
            </button>
        </div>
    );
}

function ResultView({ session, otherName, onClose }) {
    const score = session.score || 0;
    const answers = session.revealed_answers || {};
    const userIds = Object.keys(answers);
    const otherId = userIds.find((u) => u !== undefined); // fallback

    return (
        <div className="py-2" data-testid="tot-result-view">
            <div className="text-center">
                <div className="text-6xl">{score >= 80 ? "🔥" : score >= 60 ? "✨" : score >= 40 ? "🤔" : "😅"}</div>
                <div className="mt-2 font-display text-5xl font-black tracking-tighter">
                    <span className="text-gradient-purple font-fancy">{score}%</span>
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                    {score >= 80 ? "roommate soulmates 💯" : score >= 60 ? "solid vibes" : score >= 40 ? "some work but doable" : "opposites attract? 👀"}
                </div>
            </div>

            {/* Question-by-question reveal */}
            <div className="mt-5 space-y-2 max-h-[35vh] overflow-y-auto">
                {session.questions.map((q, i) => {
                    const users = Object.keys(answers);
                    const uA = users[0];
                    const uB = users[1];
                    const ansA = answers[uA]?.[String(q.id)];
                    const ansB = answers[uB]?.[String(q.id)];
                    const same = ansA && ansB && ansA === ansB;
                    return (
                        <div key={q.id} className={`rounded-2xl border p-3 ${same ? "border-primary bg-primary/5" : "border-border bg-card"}`}>
                            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">
                                Q{i + 1} · {q.prompt}
                            </div>
                            <div className="flex items-center justify-between text-sm font-semibold">
                                <span>{ansA === "a" ? q.a : ansA === "b" ? q.b : "—"}</span>
                                {same ? <Check className="h-4 w-4 text-primary" strokeWidth={3} /> : <span className="text-muted-foreground">vs</span>}
                                <span>{ansB === "a" ? q.a : ansB === "b" ? q.b : "—"}</span>
                            </div>
                        </div>
                    );
                })}
            </div>

            <button
                onClick={onClose}
                data-testid="tot-done-btn"
                className="mt-5 w-full h-12 rounded-2xl bg-primary text-primary-foreground font-black lift"
            >
                Back to chat
            </button>
        </div>
    );
}
